import React, { useState } from 'react';
import useProductResources from '../hooks/useProductResources';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import SyncModal from '../components/SyncModal';
import { localeLabel } from '../utils/localeFlag';

// gidLeaf — Pitfall 4: Phase 20 rows carry FULL Shopify GIDs in
// `parent_product_id` (e.g. `gid://shopify/Product/1234567890`). The PIM
// `/api/transly/products/` endpoint keys results by BARE numeric `parent_id`
// (e.g. `'1234567890'`). The PIM JOIN merge in this page is client-side
// (D-07), so consumers MUST convert with `gidLeaf(row.parent_product_id)`
// before looking up `pimByParentId[...]`. Failing this returns undefined and
// silently breaks vendor / product_type rendering.
const gidLeaf = (gid) => (gid ? gid.split('/').pop() : '');

/**
 * Shopify Admin URL helper for products. ADAPTED from
 * ContentResources.jsx:23-29 — collapses to ONE branch because Shopify's
 * Admin UI doesn't expose standalone pages for PRODUCT_OPTION /
 * PRODUCT_OPTION_VALUE rows. Option + value rows therefore link to the
 * parent product in admin, same as the PRODUCT row itself.
 */
function shopifyAdminUrl(resourceId, resourceType, parentProductId) {
  const targetGid = resourceType === 'PRODUCT' ? resourceId : parentProductId;
  const id = targetGid ? targetGid.split('/').pop() : '';
  return `https://admin.shopify.com/store/lamptwist-live/products/${id}`;
}

const POLICY_FILTERS = [
  { id: 'all', label: 'All policies' },
  { id: 'translate', label: 'Translate' },
  { id: 'skip', label: 'Skip' },
  { id: 'auto-skipped', label: 'Auto-skipped' },
];

// D-15 Phase 20 vocabulary — pushed/pending/failed/missing.
// MUST NOT include the legacy PIM `translated/partial/outdated` strings; the
// per-row badges and this filter chip share one mental model.
const STATUS_FILTERS = [
  { id: 'all', label: 'All statuses' },
  { id: 'pushed', label: 'Pushed' },
  { id: 'pending', label: 'Pending' },
  { id: 'failed', label: 'Failed' },
  { id: 'missing', label: 'Missing' },
];

const RESOURCE_TYPE_FILTERS = [
  { id: 'PRODUCT', label: 'Product' },
  { id: 'PRODUCT_OPTION', label: 'Option' },
  { id: 'PRODUCT_OPTION_VALUE', label: 'Option value' },
  { id: '', label: 'All types' },
];

// Shopify product state (PIM `p_status`) filter — maps to the backend
// `?status=` query param on /api/transly/products/. Default is ACTIVE so
// operators don't see drafts/archives in the translation backlog.
const PRODUCT_STATE_FILTERS = [
  { id: 'ACTIVE', label: 'Active' },
  { id: 'DRAFT', label: 'Draft' },
  { id: 'ARCHIVED', label: 'Archived' },
  { id: '', label: 'All states' },
];

// Pill styling for the per-row product-state cell. Same Tailwind palette
// shape as the StatusBadge map above, intentionally inlined per D-12 +
// the UI-SPEC inline-duplication invariant.
function ProductStatePill({ state }) {
  if (!state) return <span className="text-muted-foreground">—</span>;
  const map = {
    ACTIVE:   { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Active' },
    DRAFT:    { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',          label: 'Draft' },
    ARCHIVED: { className: 'bg-amber-100 text-amber-800 hover:bg-amber-100',       label: 'Archived' },
  };
  const cfg = map[state] || { className: 'bg-muted text-muted-foreground', label: state };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

// Group key = leading title segment (before the first ' > ').
// Articles/pages rarely use the ' > ' convention, but preserve the helper
// for parity with Collections.jsx so prefix grouping degrades gracefully into
// "one prefix == one resource" when no titles have separators.
function groupKeyForTitle(title) {
  if (!title) return '—';
  const idx = title.indexOf(' > ');
  return idx === -1 ? title : title.slice(0, idx);
}

// Per-row status badge (used in the expanded per-field view) — LIFTED
// VERBATIM from ContentResources.jsx:81-91 per D-12. The Phase 20 vocab
// (`pushed | pending | failed`) MATCHES this map; legacy
// `translated/partial/outdated` are NOT supported here on purpose.
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

// A row "needs translation" iff there's something to translate AND its
// effective policy resolves to translate. Mirrors Collections / ContentResources
// — explicit field-default / row-override wins over auto_skip_reason, and an
// empty raw_value short-circuits (sync's preserve-user-override CASE can leave
// translation_policy='translate' on a row whose source was later cleared in
// Shopify).
function needsTranslation(row) {
  if (!row.raw_value) return false;
  const effective = row.effective_policy;
  if (effective === 'skip') return false;
  if (effective === 'translate') return true;
  if (row.auto_skip_reason) return false;
  return true;
}

// Roll up a resource's fields into a per-locale status for the parent row.
function rollupForLocale(fields, locale) {
  const statusKey = `${locale}_status`;
  let needed = 0, pushed = 0, failed = 0;
  for (const f of fields) {
    if (!needsTranslation(f)) continue;
    needed++;
    if (f[statusKey] === 'pushed') pushed++;
    else if (f[statusKey] === 'failed') failed++;
  }
  return { needed, pushed, failed };
}

// "100%" when every translatable field is pushed. Otherwise the absolute
// count + a percentage subtitle so partials are obvious at a glance. Red on
// any failure, emerald on full coverage, amber while in progress.
function RollupBadge({ rollup }) {
  const { needed, pushed, failed } = rollup;
  if (needed === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const pct = Math.round((pushed / needed) * 100);
  if (failed > 0) {
    return (
      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
        {pushed}/{needed} · {failed} failed
      </Badge>
    );
  }
  if (pushed === needed) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        100% ({pushed}/{needed})
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
      {pct}% ({pushed}/{needed})
    </Badge>
  );
}

function truncate(value, n = 80) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

function dropdownValue(row) {
  if (row.auto_skip_reason === null && row.translation_policy !== null) {
    return row.translation_policy;
  }
  return 'auto';
}

/**
 * Phase 21 Products list page (new view, default-on per D-02).
 *
 * Mirrors `ContentResources.jsx` (Phase 18) 1:1 with 5 documented divergences:
 *  1. NO `resourceType` prop — single-page entry; resource_type is a chip filter.
 *  2. Two-tier expand for product → options+values inline (D-05) instead of the
 *     prefix → resource → fields three-tier of the articles/pages template.
 *  3. PIM JOIN client-side merge per row via `pimByParentId[gidLeaf(...)]`
 *     (D-07, Pitfall 4) — vendor / product_type rendered from PIM.
 *  4. D-08 filter chips (search + vendor + resource_type + policy + status);
 *     no field_key chip; no has_html chip.
 *  5. D-15 Phase 20 status vocab (`pushed/pending/failed/missing`) on the
 *     status filter chip — NOT the legacy PIM `translated/partial/outdated`.
 *
 * Inline helpers (StatusBadge, truncate, dropdownValue, groupKeyForTitle,
 * RollupBadge, needsTranslation, rollupForLocale) lifted VERBATIM from
 * ContentResources.jsx:23-139 per D-12 + UI-SPEC inline-duplication invariant.
 *
 * D-09 KNOWN LIMITATION: this page iterates `product_resources` rows as the
 * primary loop. Products that exist in PIM (`pim_product_master`) but lack a
 * `product_resources` row will NOT appear in the list. Operators see the
 * "No products found. Sync from Shopify" empty-state CTA when a fresh deploy
 * has zero `product_resources` rows. A future iteration may invert the loop
 * (PIM rows as primary, product_resources as enrichment) per Pitfall 6 to
 * surface unsynced products with a per-row "Sync required" hint.
 */
export default function ProductResources() {
  const navigate = useNavigate();

  const {
    rows,
    groupedRows,
    total,
    loading,
    error,
    resourceTypeFilter,
    setResourceTypeFilter,
    policy,
    setPolicy,
    status,
    setStatus,
    vendor,
    setVendor,
    productState,
    setProductState,
    localeFilter,
    setLocaleFilter,
    q,
    setQ,
    page,
    setPage,
    pageSize,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    updatePolicy,
    fetchData,
    triggerSync,
    childrenByProduct,
    fetchChildren,
    loadingChildren,
    pimByParentId,
    pimVendors,
    pimLocaleStatuses,
  } = useProductResources();

  const { locales } = useTargetLocales();

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  // D-05 lazy-load expand state — keyed by FULL Shopify GID. Component-local
  // React state; resets on unmount (Pitfall 7 acceptable — sessions are short
  // and the cache is cheap to repopulate).
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = async (productGid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(productGid)) next.delete(productGid);
      else next.add(productGid);
      return next;
    });
    // D-05 lazy-load: fetch children only on FIRST expand. Subsequent
    // expand/collapse cycles within the same mount hit the
    // `childrenByProduct[productGid]` cache and skip the round-trip.
    if (!childrenByProduct[productGid] && !loadingChildren.has(productGid)) {
      await fetchChildren(productGid);
    }
  };

  // Per D-06, the policy dropdown's "Auto" option maps to a NULL policy at
  // the API. Hook owns optimistic update + rollback; this wrapper surfaces
  // the failure to the operator with an alert (lifted verbatim from
  // ContentResources.jsx:215-223).
  const handlePolicyChange = async (productResourceId, newValue) => {
    const apiValue = newValue === 'auto' ? null : newValue;
    try {
      await updatePolicy(productResourceId, apiValue);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'unknown error';
      alert(`Failed to update policy: ${msg}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-foreground">Products</h1>
        <p className="text-sm text-muted-foreground">
          Per-resource translation policy and per-locale status.{' '}
          {lastSynced
            ? `Last synced: ${new Date(lastSynced).toLocaleString()}`
            : 'Never synced'}
        </p>
      </div>

      <Card>
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search title or value..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <select
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          <option value="">All vendors</option>
          {pimVendors.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <select
          value={resourceTypeFilter}
          onChange={(e) => setResourceTypeFilter(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {RESOURCE_TYPE_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <select
          value={policy}
          onChange={(e) => setPolicy(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {POLICY_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <select
          value={productState}
          onChange={(e) => setProductState(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
          title="Shopify product state (Active / Draft / Archived)"
        >
          {PRODUCT_STATE_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
        <Button
          onClick={() => setSyncModalOpen(true)}
          variant="outline"
          size="sm"
        >
          Sync from Shopify
        </Button>
      </div>
      <CardContent>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
            {error}
          </div>
        )}
        {loading && <div className="p-4 text-sm text-muted-foreground">Loading...</div>}
        {!loading && groupedRows.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              No products found. Run a sync from Shopify to populate the inventory.
            </p>
            <Button onClick={() => setSyncModalOpen(true)}>
              Sync from Shopify
            </Button>
          </div>
        )}
        {!loading && groupedRows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="w-24">State</TableHead>
                <TableHead>Policy</TableHead>
                {locales.map((loc) => (
                  <TableHead key={loc} className="w-20">{localeLabel(loc)}</TableHead>
                ))}
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupedRows.map((group) => {
                const productGid = group.parent_product_id;
                const displayRow = group.displayRow;
                const isOpen = expanded.has(productGid);
                // PIM JOIN — Pitfall 4: PIM map is keyed by BARE numeric
                // parent_id, not GID. Always go through gidLeaf().
                const pim = pimByParentId[gidLeaf(productGid)];
                const childRows = childrenByProduct[productGid] || [];
                const isLoadingChildren = loadingChildren.has(productGid);
                const policyLabel = {
                  auto: 'Auto',
                  translate: 'Translate',
                  skip: 'Skip',
                  mixed: 'Mixed',
                }[group.policyRollup];

                return (
                  <React.Fragment key={productGid}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      // D-10 / Pitfall 9: navigate to the NEW drill-in route
                      // `/products/:productId/resources` (Wave 2 App.jsx).
                      // NEVER navigate to `/products/:id` (legacy route).
                      onClick={() => navigate(`/products/${gidLeaf(productGid)}/resources`)}
                    >
                      <TableCell>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(productGid);
                          }}
                          className="p-1 hover:bg-muted rounded"
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                        >
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          />
                        </button>
                      </TableCell>
                      <TableCell className="font-medium">
                        {truncate(displayRow.title || displayRow.raw_value || pim?.p_title, 60)}
                      </TableCell>
                      <TableCell>{pim?.p_vendor || '—'}</TableCell>
                      <TableCell>{pim?.p_product_type || '—'}</TableCell>
                      <TableCell><ProductStatePill state={displayRow.p_status || pim?.p_status} /></TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {policyLabel}
                      </TableCell>
                      {locales.map((loc) => (
                        <TableCell key={loc}>
                          <RollupBadge rollup={rollupForLocale(group.fieldRows, loc)} />
                        </TableCell>
                      ))}
                      <TableCell>
                        <a
                          href={shopifyAdminUrl(productGid, 'PRODUCT', productGid)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-muted-foreground hover:text-foreground inline-flex items-center"
                          aria-label="Open in Shopify Admin"
                          title="Open in Shopify Admin"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </TableCell>
                    </TableRow>

                    {isOpen && isLoadingChildren && childRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7 + locales.length} className="pl-12 text-sm text-muted-foreground">
                          Loading options...
                        </TableCell>
                      </TableRow>
                    )}

                    {isOpen && childRows.map((child) => (
                      <TableRow key={child.id} className="bg-muted/20">
                        <TableCell></TableCell>
                        <TableCell className="pl-8 text-sm" colSpan={5}>
                          <span className="text-muted-foreground mr-2">
                            {child.resource_type === 'PRODUCT_OPTION' ? '↳ option' : '↳ value'}
                          </span>
                          {truncate(child.title || child.raw_value, 60)}
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({child.field_key})
                          </span>
                        </TableCell>
                        {locales.map((loc) => (
                          <TableCell key={loc}><StatusBadge status={child[`${loc}_status`]} /></TableCell>
                        ))}
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!loading && groupedRows.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {groupedRows.length} on this page · {total.toLocaleString()} total
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      </Card>

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/product-resources/sync/"
        statusEndpoint="/api/transly/product-resources/sync-status/"
        title="Syncing Products"
      />
    </div>
  );
}
